import type { Row } from '../types';
import { PERSON_FIELDS, type PersonField } from './rules';
import { missingOf, type MissingItem, type PersonResult, type RecordResult } from './people';

// ── Row assembly (Phase G) ───────────────────────────────────────────────────────────────────────

const describe = (items: MissingItem[]) =>
  items
    .map((i) => (i.issue ? `${i.field} (${i.issue} date)` : i.field))
    .join(', ');

export function missingText(r: RecordResult): string {
  if (r.failed) return 'detail lookup failed';
  const parts: string[] = [];
  const a = missingOf(r.a);
  if (a.length) parts.push(`A: ${describe(a)}`);
  if (r.b) {
    const b = missingOf(r.b);
    if (b.length) parts.push(`B: ${describe(b)}`);
  }
  if (r.requireLocation && !r.location) parts.push('location');
  return parts.join('; ');
}

function filledBy(p: PersonResult): string {
  const s2 = PERSON_FIELDS.filter((f) => p.from[f] === 2);
  const s3 = PERSON_FIELDS.filter((f) => p.from[f] === 3);
  return [s2.length ? 'source 2' : '', s3.length ? `${s3.join('/')} from source 3` : '']
    .filter(Boolean).join(', ');
}

export function filledFromText(r: RecordResult): string {
  if (r.failed) return '';
  const parts: string[] = [];
  const a = filledBy(r.a);
  if (a) parts.push(`A: ${a}`);
  if (r.b) {
    const b = filledBy(r.b);
    if (b) parts.push(`B: ${b}`);
  }
  return parts.join('; ');
}

/** List row + Person A in the normal columns + Person B in the "B" twins. */
export function buildRow(list: Row, r: RecordResult | undefined): Row {
  if (!r) return { ...list };
  const out: Row = { ...list };
  for (const f of PERSON_FIELDS) {
    out[f] = r.a.values[f] ?? '';
    out[`${f}B`] = r.b?.values[f] ?? '';
  }
  out.locationName = r.location ?? '';
  out.missingFields = missingText(r);
  out.filledFrom = filledFromText(r);
  return out;
}

// ── Review modal data (Phase E) ──────────────────────────────────────────────────────────────────

export interface LookupPlan {
  people: number;
  withMemberID: number;
  byName: number;
  /** No member id and no full name, so Source 3 cannot even try. */
  noRoute: number;
}

export interface ReviewLine {
  who: string;
  /** Known name of this person, when there is one. */
  name?: string;
  fields: string;
  /** Values that were present but unusable, exactly as received (e.g. a bad date of birth). */
  found?: { field: string; issue: string; value: string }[];
}

export interface ReviewItem {
  id: number;
  reference: string;
  lines: ReviewLine[];
}

export interface Review {
  items: ReviewItem[];
  failed: { id: number; reference: string }[];
  requestsAffected: number;
  peopleAffected: number;
  fieldsMissing: number;
  plan: LookupPlan;
}

export function buildReview(results: RecordResult[]): Review {
  const items: ReviewItem[] = [];
  const failed: Review['failed'] = [];
  const plan: LookupPlan = { people: 0, withMemberID: 0, byName: 0, noRoute: 0 };
  let peopleAffected = 0;
  let fieldsMissing = 0;

  for (const r of results) {
    if (r.failed) { failed.push({ id: r.id, reference: r.reference }); continue; }
    const lines: ReviewLine[] = [];

    ([['Person A', r.a], ['Person B', r.b]] as const).forEach(([who, p]) => {
      if (!p) return;
      const miss = missingOf(p);
      if (miss.length === 0) return;
      const found = miss
        .filter((m) => m.issue && p.rawIssues?.[m.field as PersonField])
        .map((m) => ({ field: m.field, issue: String(m.issue), value: p.rawIssues![m.field as PersonField]! }));
      const name = [p.values.firstName, p.values.surname].filter(Boolean).join(' ');
      lines.push({ who, name: name || undefined, fields: describe(miss), found: found.length ? found : undefined });
      peopleAffected += 1;
      fieldsMissing += miss.length;
      plan.people += 1;
      if (p.memberID != null) plan.withMemberID += 1;
      else if (p.values.firstName && p.values.surname) plan.byName += 1;
      else plan.noRoute += 1;
    });
    if (r.requireLocation && !r.location) { lines.push({ who: 'Request', fields: 'location' }); fieldsMissing += 1; }

    if (lines.length) items.push({ id: r.id, reference: r.reference, lines });
  }

  return { items, failed, requestsAffected: items.length, peopleAffected, fieldsMissing, plan };
}

// ── Summary (G3) ─────────────────────────────────────────────────────────────────────────────────

export interface EnrichSummary {
  totalRequests: number;
  complete: number;
  peopleFromSource2: number;
  peopleFromSource3: number;
  peopleIncomplete: number;
  lookupsFailed: number;
  overTwoPeople: number;
}

export function buildSummary(results: RecordResult[]): EnrichSummary {
  const s: EnrichSummary = {
    totalRequests: results.length, complete: 0, peopleFromSource2: 0, peopleFromSource3: 0,
    peopleIncomplete: 0, lookupsFailed: 0, overTwoPeople: 0,
  };
  for (const r of results) {
    if (r.failed) { s.lookupsFailed += 1; continue; }
    if (r.extraPeople > 0) s.overTwoPeople += 1;
    if (missingText(r) === '') s.complete += 1;
    [r.a, r.b].forEach((p) => {
      if (!p) return;
      const sources = PERSON_FIELDS.map((f) => p.from[f]);
      if (sources.includes(2)) s.peopleFromSource2 += 1;
      if (sources.includes(3)) s.peopleFromSource3 += 1;
      if (missingOf(p).length > 0) s.peopleIncomplete += 1;
    });
  }
  return s;
}
